// Plan-BundlesImplementation §E.3 — owner-only auto-overage settings.
//
// Replaces the "Coming soon" placeholder section in BillingPage.tsx.
// Per §A.7 defaults (chat/email/knowledge/scrapes/reports ON,
// voice OFF — operator opt-in). LiveAvatar deliberately NOT a toggle
// (hard-block always per §A.7 + §B.7).
//
// On enable: confirmation modal explains the per-event rate from §A.8.
// On disable: applied immediately (no destructive risk).
//
// Wires to `billingAPI.toggleAutoOverage` →
// `POST /api/billing/workspace/{workspace_id}/auto-overage`.
//
// ⚠️ Key contract (post-Storm `b1113a3`): we use CANONICAL keys
// (`chat_conversations`, `email_threads`, etc.) — these match
// `workspaces.auto_overage.<canonical>` storage AND
// `consume_resource()` read order. Storm's normalizer accepts both
// short and canonical on the write path, but reads stay canonical-only
// (no fallback for legacy short-key data, which `consume_resource()`
// ignored anyway — that data was already dead). LiveAvatar canonical
// is `liveavatar_minutes` (NOT included in this list per §A.7).

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X, AlertTriangle, MessageSquare, Mail, BookOpen, Globe, FileText, Mic } from 'lucide-react';
import { billingAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useBilling } from '../../contexts/BillingContext';
import type { WorkspaceBilling } from '../../types/billing';
import { isPooledBilling } from '../../types/billing';

interface Props {
  billing: WorkspaceBilling;
  workspaceId: string;
  isOwner: boolean;
}

interface OverageResource {
  key: string; // backend resource key
  label: string;
  rate: string; // per-event auto-overage rate from §A.8
  icon: React.ReactNode;
}

// Order matches plan §A.8. Voice last because it's the §A.7 default-OFF
// one. Keys are CANONICAL — match `workspaces.auto_overage.<key>` +
// `consume_resource()` lookup. See file-header note for context.
const RESOURCES: OverageResource[] = [
  {
    key: 'chat_conversations',
    label: 'Chat conversations',
    rate: '$0.012 / chat',
    icon: <MessageSquare className="w-4 h-4" />,
  },
  {
    key: 'email_threads',
    label: 'Email threads',
    rate: '$0.012 / thread',
    icon: <Mail className="w-4 h-4" />,
  },
  {
    key: 'knowledge_summaries',
    label: 'Knowledge summaries',
    rate: '$0.025 / summary',
    icon: <BookOpen className="w-4 h-4" />,
  },
  {
    key: 'web_scrapes',
    label: 'Web scrapes',
    rate: '$0.035 / scrape',
    icon: <Globe className="w-4 h-4" />,
  },
  {
    key: 'reports',
    label: 'Reports',
    rate: '$0.055 / report',
    icon: <FileText className="w-4 h-4" />,
  },
  {
    key: 'voice_minutes',
    label: 'Voice minutes',
    rate: '$0.095 / min',
    icon: <Mic className="w-4 h-4" />,
  },
];

const OverageSettingsSection: React.FC<Props> = ({ billing, workspaceId, isOwner }) => {
  const { showToast } = useToast();
  const { refetch } = useBilling();

  // Mirror the server state locally so we can render optimistic flips
  // while the PATCH is in flight. Reset whenever the canonical billing
  // payload changes.
  const [state, setState] = useState<Record<string, boolean>>({});
  const [pendingResource, setPendingResource] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<OverageResource | null>(null);

  useEffect(() => {
    setState({ ...(billing.auto_overage ?? {}) });
  }, [billing.auto_overage]);

  const apply = async (resource: string, enabled: boolean) => {
    setPendingResource(resource);
    setState((prev) => ({ ...prev, [resource]: enabled }));
    try {
      await billingAPI.toggleAutoOverage(workspaceId, resource, enabled);
      // Refetch the canonical billing payload so any server-side
      // side-effects (e.g. invoice preview) propagate.
      await refetch();
      showToast(
        enabled
          ? `Auto-overage enabled for ${RESOURCES.find((r) => r.key === resource)?.label ?? resource}`
          : 'Auto-overage disabled',
        'success',
      );
    } catch (err: any) {
      // Roll back optimistic state on failure.
      setState((prev) => ({ ...prev, [resource]: !enabled }));
      const detail = err?.response?.data?.detail;
      showToast(
        typeof detail === 'string' ? detail : 'Could not update auto-overage',
        'error',
      );
    } finally {
      setPendingResource(null);
    }
  };

  const onToggleClick = (r: OverageResource) => {
    if (!isOwner) return;
    const currentlyOn = state[r.key] === true;
    if (currentlyOn) {
      // Disable — apply immediately, no confirmation.
      apply(r.key, false);
      return;
    }
    // Enable — show confirmation modal.
    setConfirmTarget(r);
  };

  const onConfirmEnable = async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    setConfirmTarget(null);
    await apply(target.key, true);
  };

  // Plan-TextVoicePoolBilling-2026-08 §3 — hide the whole section in pooled
  // mode. Five of the six toggles are retired per-category resources, and the
  // sixth (voice) has been a dead path since voice started bypassing
  // `consume_resource()` (plan §0.3) — so there is no row left that does
  // anything. Hidden, NOT deleted: Phase D removes the section and its Stripe
  // meter events. 🚨 Placed AFTER every hook above — moving it higher breaks
  // the rules of hooks.
  if (isPooledBilling(billing)) return null;

  return (
    <>
      <section className="card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Auto-overage</h2>
          {!isOwner && (
            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
              Owner only
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          When credits run out, allow usage to continue and bill the per-event rate. LiveAvatar
          always hard-blocks — no overage available.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          {RESOURCES.map((r) => {
            const enabled = state[r.key] === true;
            const isPending = pendingResource === r.key;
            return (
              <button
                key={r.key}
                onClick={() => onToggleClick(r)}
                disabled={!isOwner || isPending}
                className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                  enabled
                    ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40'
                } ${
                  !isOwner
                    ? 'cursor-not-allowed opacity-60'
                    : 'hover:border-primary/50 cursor-pointer'
                }`}
                title={!isOwner ? 'Only workspace owners can change auto-overage settings' : ''}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={`flex items-center justify-center w-8 h-8 rounded-md flex-shrink-0 ${
                      enabled
                        ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {r.icon}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                      {r.label}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {r.rate}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                  <span
                    role="switch"
                    aria-checked={enabled}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      enabled ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        enabled ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-4 leading-relaxed">
          Once enabled, you'll receive an email when the first overage event fires in a billing
          cycle. Charges appear on your next invoice as a separate line item.
        </p>
      </section>

      {/* Confirmation modal — shown on ENABLE only. Portaled to <body>
          + z-[60] so it never collides with the BundleCards overlays
          (all share the same higher modal tier, immune to DOM order). */}
      {confirmTarget && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Enable auto-overage?
              </h3>
              <button
                onClick={() => setConfirmTarget(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 text-sm text-gray-700 dark:text-gray-300 space-y-3">
              <p>
                Enabling auto-overage for{' '}
                <span className="font-semibold">{confirmTarget.label}</span> means AskProdigy will
                charge{' '}
                <span className="font-semibold text-amber-700 dark:text-amber-400">
                  {confirmTarget.rate}
                </span>{' '}
                after your monthly allowance + top-up credits are exhausted.
              </p>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  Charges accrue silently — you'll only see them on your next invoice. You can
                  disable this any time.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                onClick={() => setConfirmTarget(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={onConfirmEnable}
                className="px-4 py-2 rounded-lg text-white font-semibold text-sm bg-gradient-to-r from-amber-500 to-orange-600 hover:shadow-lg"
              >
                Enable auto-overage
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default OverageSettingsSection;
